import { supabase } from './supabase'

/**
 * Загрузить фотографию в Supabase Storage
 * @param {File|string} file - File объект или base64 строка
 * @param {string} userId - ID пользователя
 * @param {number} index - Индекс фотографии (0, 1, 2...)
 * @param {{ uniquePath?: boolean }} [opts] - uniquePath: true = не перезаписывать, новый URL (обход кэша на мобилках)
 * @returns {Promise<string>} URL загруженной фотографии
 */
/**
 * Вычислить размер файла в байтах
 */
function getFileSize(file) {
  if (typeof file === 'string') {
    // Base64 строка: размер примерно на 33% больше оригинала
    const base64Data = file.split(',')[1]
    return Math.floor((base64Data.length * 3) / 4)
  } else {
    // File объект
    return file.size
  }
}

export async function uploadPhoto(file, userId, index = 0, opts = {}) {
  try {
    const MAX_SIZE = 10 * 1024 * 1024 // 10MB
    const fileSize = getFileSize(file)
    if (fileSize > MAX_SIZE) {
      throw new Error(`File size (${Math.round(fileSize / 1024 / 1024)}MB) exceeds the limit of 10MB`)
    }

    let fileData
    let fileName
    const unique = opts.uniquePath === true
    const suffix = unique ? `-${Date.now()}` : ''

    if (typeof file === 'string') {
      const base64Data = file.split(',')[1]
      const mimeMatch = file.match(/data:([^;]+);base64/)
      const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg'
      const extension = mimeType.split('/')[1] || 'jpg'
      fileData = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0))
      fileName = `${userId}/${index}${suffix}.${extension}`
    } else {
      const extension = file.name.split('.').pop() || 'jpg'
      fileName = `${userId}/${index}${suffix}.${extension}`
      const arrayBuffer = await file.arrayBuffer()
      fileData = new Uint8Array(arrayBuffer)
    }

    console.log(`[uploadPhoto] Uploading photo ${index} for user ${userId}, size: ${Math.round(fileSize / 1024)}KB`)

    const { data, error } = await supabase.storage
      .from('photos')
      .upload(fileName, fileData, {
        contentType: file instanceof File ? file.type : 'image/jpeg',
        upsert: true
      })

    if (error) {
      console.error(`[uploadPhoto] Upload error for photo ${index}:`, error)
      throw error
    }

    // Получаем публичный URL
    const { data: urlData } = supabase.storage
      .from('photos')
      .getPublicUrl(fileName)

    console.log(`[uploadPhoto] ✅ Photo ${index} uploaded successfully:`, urlData.publicUrl)
    return urlData.publicUrl
  } catch (error) {
    console.error(`[uploadPhoto] Error uploading photo ${index}:`, error)
    throw error
  }
}

/**
 * Загрузить фото для сообщения в чате (отдельная папка chat/userId/)
 * @param {File} file - файл изображения
 * @param {string} userId - ID отправителя
 * @returns {Promise<string>} публичный URL фото
 */
export async function uploadChatPhoto(file, userId) {
  // Валидация типа файла
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid file type. Only JPEG, PNG, and WebP images are allowed.')
  }

  // Валидация размера файла (максимум 10MB)
  const MAX_SIZE = 10 * 1024 * 1024 // 10MB для чата
  if (file.size > MAX_SIZE) {
    throw new Error('File size exceeds 10MB')
  }

  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg'
  const fileName = `${userId}/chat/${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`
  const arrayBuffer = await file.arrayBuffer()
  const fileData = new Uint8Array(arrayBuffer)

  const { error } = await supabase.storage
    .from('photos')
    .upload(fileName, fileData, { contentType: file.type || 'image/jpeg', upsert: false })

  if (error) throw error

  const { data: urlData } = supabase.storage.from('photos').getPublicUrl(fileName)
  return urlData.publicUrl
}

/**
 * Удалить фотографию из Supabase Storage
 * @param {string} userId - ID пользователя
 * @param {number} index - Индекс фотографии
 */
export async function deletePhoto(userId, index) {
  try {
    // Нужно найти файл по паттерну, так как мы не знаем расширение
    const { data: files, error: listError } = await supabase.storage
      .from('photos')
      .list(userId, {
        search: `${index}.`
      })

    if (listError) throw listError

    if (files && files.length > 0) {
      const filePath = `${userId}/${files[0].name}`
      const { error: deleteError } = await supabase.storage
        .from('photos')
        .remove([filePath])

      if (deleteError) throw deleteError
    }
  } catch (error) {
    console.error('Error deleting photo:', error)
    throw error
  }
}

/**
 * Удалить все фотографии пользователя
 * @param {string} userId - ID пользователя
 */
export async function deleteAllPhotos(userId) {
  try {
    const { data: files, error: listError } = await supabase.storage
      .from('photos')
      .list(userId)

    if (listError) throw listError

    if (files && files.length > 0) {
      const filePaths = files.map(f => `${userId}/${f.name}`)
      const { error: deleteError } = await supabase.storage
        .from('photos')
        .remove(filePaths)

      if (deleteError) throw deleteError
    }
  } catch (error) {
    console.error('Error deleting all photos:', error)
    throw error
  }
}
